import React, { useRef, useMemo, useCallback } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import DOMPurify from 'dompurify';

interface RichTextEditorProps {
  value: string;
  onChange: (content: string) => void;
  placeholder?: string;
  height?: number;
  disabled?: boolean;
  mode?: 'basic' | 'full';
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Γράψτε το κείμενό σας εδώ...',
  height = 300,
  disabled = false,
  mode = 'full'
}: RichTextEditorProps) {
  const editorRef = useRef<any>(null);

  // Use callback to prevent re-creation on every render
  const handleEditorChange = useCallback((content: string, editor: any) => {
    // Only update if content actually changed to prevent loops
    if (content !== value) {
      onChange(content);
    }
  }, [value, onChange]);

  const basicToolbar = 'bold italic underline | bullist numlist | link | removeformat';
  
  const fullToolbar = `
    undo redo | formatselect | bold italic underline strikethrough | 
    forecolor backcolor | alignleft aligncenter alignright alignjustify | 
    bullist numlist outdent indent | link image | 
    blockquote codesample | table | removeformat | help
  `;

  // Minimal plugins for better performance
  const basicPlugins = ['autolink', 'lists', 'link'];
  
  const fullPlugins = [
    'autolink', 'lists', 'link', 'image', 'charmap',
    'searchreplace', 'code', 'table', 'help',
    'wordcount', 'codesample'
  ];

  // Memoized init configuration
  const initConfig = useMemo(() => ({
    height: height,
    menubar: mode === 'full',
    plugins: mode === 'full' ? fullPlugins : basicPlugins,
    toolbar: mode === 'full' ? fullToolbar : basicToolbar,
    placeholder: placeholder,
    
    // Performance optimizations
    browser_spellcheck: true,
    contextmenu: false,
    
    // Content styling
    content_style: `
      body { 
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        font-size: 14px;
        line-height: 1.6;
        color: #374151;
      }
      p { margin: 0 0 1em 0; }
      blockquote {
        border-left: 4px solid #3B82F6;
        padding-left: 1em;
        margin-left: 0;
        font-style: italic;
        color: #6B7280;
      }
      pre {
        background: #F3F4F6;
        padding: 1em;
        border-radius: 4px;
        overflow-x: auto;
      }
      code {
        background: #F3F4F6;
        padding: 2px 4px;
        border-radius: 3px;
        font-family: 'Courier New', monospace;
        font-size: 0.9em;
      }
      table {
        border-collapse: collapse;
        width: 100%;
        margin: 1em 0;
      }
      table th, table td {
        border: 1px solid #D1D5DB;
        padding: 8px;
      }
      table th {
        background: #F3F4F6;
        font-weight: 600;
      }
      img {
        max-width: 100%;
        height: auto;
      }
      a {
        color: #3B82F6;
        text-decoration: none;
      }
      a:hover {
        text-decoration: underline;
      }
    `,
    
    // Simplified setup
    setup: (editor: any) => {
      // Add medical notation shortcuts when editor is ready
      editor.on('init', () => {
        editor.addShortcut('ctrl+shift+m', 'Insert μm', () => {
          editor.insertContent('μm');
        });
        editor.addShortcut('ctrl+shift+x', 'Insert χ magnification', () => {
          editor.insertContent('χ');
        });
      });
    },
    
    // Simplified paste settings
    paste_data_images: true,
    paste_as_text: false,
    
    // CRITICAL: Set automatic uploads to true and configure properly
    automatic_uploads: true,
    images_upload_credentials: true,
    images_reuse_filename: false,
    images_upload_base_path: '',
    file_picker_types: 'image',
    
    // Image upload handler - Fixed version
    images_upload_handler: (blobInfo: any, progress: any) => new Promise((resolve, reject) => {
      // Create a FormData object
      const formData = new FormData();
      formData.append('file', blobInfo.blob(), blobInfo.filename());
      
      // Get auth token - your app stores it directly as 'accessToken'
      const accessToken = localStorage.getItem('accessToken');
      
      if (!accessToken) {
        console.error('No access token found in localStorage');
        reject('Απαιτείται σύνδεση για τη μεταφόρτωση εικόνων');
        return;
      }
      
      console.log('Found access token for image upload');

      // Create XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          progress(percentComplete);
        }
      };
      
      // Handle successful upload
      xhr.onload = () => {
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success && response.url) {
              resolve(response.url);
            } else {
              reject(response.error || 'Upload failed');
            }
          } catch (error) {
            reject('Invalid server response');
          }
        } else {
          reject(`HTTP Error: ${xhr.status}`);
        }
      };
      
      // Handle upload error
      xhr.onerror = () => {
        reject('Upload failed due to network error');
      };
      
      // Configure and send request to consolidated forum endpoint
      xhr.open('POST', '/api/v1/forum?path=upload');
      xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
      xhr.send(formData);
    }),
    
    // Important: Keep blob URLs valid
    convert_urls: false,
    relative_urls: false,
    remove_script_host: false,
    
    // Status bar settings
    statusbar: true,
    branding: false,
    elementpath: false,
    resize: true,
    
    // Mobile settings
    mobile: {
      menubar: false,
      plugins: ['autolink', 'lists', 'link'],
      toolbar: 'bold italic | bullist numlist | link'
    },
    
    // Additional settings for stability
    entity_encoding: 'raw',
    verify_html: false,
    forced_root_block: 'p',
    remove_trailing_brs: false
    
  }), [mode, height, placeholder, fullPlugins, basicPlugins, fullToolbar, basicToolbar]);

  return (
    <div className="rich-text-editor">
      <Editor
        apiKey="vw6i94swz4hif8sao2h3gegzcflkwvchaoflpaihuaervgfi"
        onInit={(evt, editor) => {
          editorRef.current = editor;
        }}
        value={value}
        onEditorChange={handleEditorChange}
        disabled={disabled}
        init={initConfig}
      />
      
      <style jsx>{`
        .rich-text-editor {
          border: 1px solid #D1D5DB;
          border-radius: 8px;
          overflow: hidden;
        }
        
        .rich-text-editor.error {
          border-color: #EF4444;
        }
        
        :global(.tox-tinymce) {
          border: none !important;
        }
        
        /* Prevent layout shift */
        :global(.tox) {
          font-size: 14px !important;
        }
        
        :global(.tox .tox-edit-area) {
          border: none !important;
        }
        
        /* Fix for image loading */
        :global(.tox-tinymce img) {
          max-width: 100%;
          height: auto;
        }
      `}</style>
    </div>
  );
}

// Simple HTML viewer component with sanitization
export function SafeHtmlViewer({ 
  html, 
  className = '',
  maxLength 
}: { 
  html: string; 
  className?: string;
  maxLength?: number;
}) {
  const sanitized = useMemo(() => {
    let content = html;
    if (maxLength && content.length > maxLength) {
      content = content.substring(0, maxLength) + '...';
    }
    
    return DOMPurify.sanitize(content, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li',
        'blockquote', 'pre', 'code',
        'a', 'img',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'sup', 'sub', 'span'
      ],
      ALLOWED_ATTR: [
        'href', 'src', 'alt', 'title', 'width', 'height',
        'class', 'style', 'target', 'rel'
      ],
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    });
  }, [html, maxLength]);

  return (
    <div 
      className={`prose prose-sm max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
}